import type { ReactElement } from 'react';

import type { DateTime } from 'luxon';

import PatientGroupClassesListView from '~/components/maud/patients/profile/tabs/appointments/groupClasses';
import { CalendarListTypeOption } from '~/lib/constants/appointment.constants';
import { ScheduleMode } from '~/lib/constants/schedule.constants';

import ScheduleDayView from './day-view/ScheduleDayView';
import ScheduleWeekView from './week-view/ScheduleWeekView';

interface ScheduleViewProps {
  isProvider?: boolean;
  currentDate: DateTime;
  height: string;
  lastScheduleDate?: string;
  mode: ScheduleMode | AppointmentListType;
  needReload?: boolean;
  nextScheduleDate?: string;
  onCancelAppointment?: (externalId: string) => void;
  onCancelledGroupClicked?: (startDateTime: string) => void;
  onFetchGroupClassEvents?: () => void;
  onNextScheduleDateClicked?: (nextScheduleDate: string) => void;
  onNotesClicked?: (externalId: string) => void;
  onScheduleItemClicked: (externalId: string) => void;
  onZoomClicked?: (externalId: string) => void;
  patientId?: string;
  resetNeedReload?: () => void;
  scheduledItems: ScheduleItemBase[];
  onCloseEventDetailsModal: () => void;
}

const scheduleViewComponentMapper: Record<
  ScheduleMode,
  (props: ScheduleViewProps) => ReactElement
> = {
  [ScheduleMode.DAY]: (props: ScheduleViewProps) => (
    <ScheduleDayView {...props} />
  ),
  [ScheduleMode.WEEK]: (props: ScheduleViewProps) => (
    <ScheduleWeekView {...props} />
  ),
};

const scheduleViewComponentMapperV2: Record<
  AppointmentListType,
  (props: ScheduleViewProps) => ReactElement
> = {
  [CalendarListTypeOption.DAILY_VIEW]: (props: ScheduleViewProps) => (
    <ScheduleDayView {...props} />
  ),
  [CalendarListTypeOption.WEEKLY_VIEW]: (props: ScheduleViewProps) => (
    <ScheduleWeekView {...props} />
  ),
  [CalendarListTypeOption.GROUPS]: (props: ScheduleViewProps) => (
    <PatientGroupClassesListView patientId={props.patientId} />
  ),
};

const ScheduleView = (scheduleViewProps: ScheduleViewProps) => {
  const { mode } = scheduleViewProps;

  const isScheduleModeTabs =
    mode === ScheduleMode.DAY || mode === ScheduleMode.WEEK;

  const ScheduleViewComponent = isScheduleModeTabs
    ? scheduleViewComponentMapper[mode]
    : scheduleViewComponentMapperV2[mode];
  return <ScheduleViewComponent {...scheduleViewProps} />;
};

export default ScheduleView;
